import { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Check, Bitcoin, Copy, Clock, CheckCircle2, XCircle, Loader2, Coins, AlertTriangle, CreditCard,
} from "lucide-react";

function WhatsAppIcon({ className }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.263.489 1.694.625.712.227 1.36.195 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
    </svg>
  );
}

function StatusPill({ status }) {
  const map = {
    pending: ["text-[#e0a800] bg-[#e0a800]/10", Clock, "Pending"],
    approved: ["text-[#00F593] bg-[#00F593]/10", CheckCircle2, "Approved"],
    rejected: ["text-[#FF3B30] bg-[#FF3B30]/10", XCircle, "Rejected"],
  };
  const [cls, Icon, label] = map[status] || map.pending;
  return <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 font-mono-term text-xs uppercase tracking-widest ${cls}`}><Icon className="h-3.5 w-3.5" />{label}</span>;
}

export default function Billing() {
  const { user } = useOutletContext();
  const { refreshUser } = useAuth();
  const [plans, setPlans] = useState([]);
  const [info, setInfo] = useState({ wallets: [] });
  const [payments, setPayments] = useState([]);
  const [selected, setSelected] = useState(null);
  const [method, setMethod] = useState("BTC");
  const [txid, setTxid] = useState("");
  const [busy, setBusy] = useState(false);
  const [cardBusy, setCardBusy] = useState(false);
  const [partnerPlans, setPartnerPlans] = useState([]);
  const [ppBusy, setPpBusy] = useState(null);
  const license = user?.license;

  const PLAN_LOOKUP = {
    "test-3": "sfx_test_3", "starter-7": "sfx_starter_7",
    "pro-15": "sfx_pro_15", "elite-30": "sfx_elite_30",
  };

  const payViaCard = async () => {
    const lookup = PLAN_LOOKUP[selected?.id];
    if (!lookup) { toast.error("Kat pa disponib pou plan sa a"); return; }
    setCardBusy(true);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: lookup, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setCardBusy(false);
    }
  };

  const load = async () => {
    const [p, i, m, pp] = await Promise.all([
      api.get("/plans"), api.get("/payment/info"), api.get("/payments/me"),
      api.get("/payments/my-partner-plans").catch(() => ({ data: [] })),
    ]);
    setPlans(p.data); setInfo(i.data); setPayments(m.data); setPartnerPlans(pp.data || []);
  };
  useEffect(() => { load(); }, []);

  const payPartnerPlan = async (planId) => {
    setPpBusy(planId);
    try {
      const { data } = await api.post("/payments/checkout-partner-plan", { partner_plan_id: planId, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setPpBusy(null);
    }
  };

  const payViaWhatsApp = async () => {
    setBusy(true);
    const msg = encodeURIComponent(
      `Bonjou! Mwen fè yon peman Smart FX pou plan "${selected.name}" ($${selected.price}) pa ${method}. ` +
      `Men screenshot prèv peman an:`);
    const url = `${info.whatsapp_url}?text=${msg}`;
    const waWindow = window.open("", "_blank");
    try {
      await api.post("/payments", { plan_id: selected.id, method, txid,
        note: "Prèv peman ap voye sou WhatsApp" });
      if (waWindow) waWindow.location = url;
      else window.location.href = url;
      toast.success("Peman anrejistre. Voye screenshot la sou WhatsApp — verifikasyon nan 24 èdtan.");
      setSelected(null); setTxid("");
      await load();
      await refreshUser();
    } catch (e) {
      if (waWindow) waWindow.close();
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const activeWallet = info.wallets?.find((w) => w.method === method) || info.wallets?.[0];
  const copyAddr = () => { navigator.clipboard.writeText(activeWallet?.address || ""); toast.success("Address copied"); };

  const sortedPlans = [...plans].sort((a, b) => a.days - b.days);

  const PlanCard = ({ plan }) => (
    <div data-testid={`billing-plan-${plan.id}`} className={`relative bg-[#0F0F0F] border p-6 flex flex-col ${plan.popular ? "border-white" : "border-[#222]"}`}>
      {plan.popular && <span className="absolute -top-3 left-6 bg-white text-black text-xs font-semibold px-3 py-1 font-mono-term uppercase tracking-widest">Popular</span>}
      <div className="flex items-baseline justify-between">
        <h3 className="font-heading text-lg font-semibold">{plan.name}</h3>
        <span className="font-mono-term text-xs text-[#888]">{plan.days}d</span>
      </div>
      <div className="mt-3 mb-4"><span className="font-heading text-3xl font-bold">${plan.price}</span></div>
      <ul className="space-y-2 flex-1 mb-5">
        {plan.features.slice(0, 5).map((f) => <li key={f} className="flex items-start gap-2 text-xs text-[#ccc]"><Check className="h-3.5 w-3.5 text-[#00F593] mt-0.5 shrink-0" />{f}</li>)}
      </ul>
      <Button onClick={() => setSelected(plan)} data-testid={`select-plan-${plan.id}`}
        className={`w-full h-10 rounded-sm font-semibold transition-colors ${plan.popular ? "bg-white text-black hover:bg-[#e5e5e5]" : "bg-[#1A1A1A] text-white hover:bg-[#252525] border border-[#333]"}`}>
        {license?.plan_id === plan.id && license?.active ? "Current Plan" : "Select & Pay"}
      </Button>
    </div>
  );

  return (
    <div className="px-5 pt-6 pb-6 fade-up space-y-8">
      <div>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Subscription</h1>
        <p className="text-[#888] text-sm mt-1">Peye ak crypto, epi voye prèv ou sou WhatsApp pou apwobasyon (jiska 24 èdtan).</p>
      </div>

      <div className="border border-[#222] bg-[#0F0F0F] px-6 py-5 flex flex-wrap items-center justify-between gap-4" data-testid="current-license">
        <div>
          <span className="font-mono-term text-xs uppercase tracking-widest text-[#666]">Current Plan</span>
          <div className="font-heading text-xl font-bold mt-1">{license?.active ? license.plan_name : "No active subscription"}</div>
        </div>
        <div className="text-right">
          <span className="font-mono-term text-xs uppercase tracking-widest text-[#666]">Status</span>
          <div className={`font-heading font-bold mt-1 ${license?.active ? "text-[#00F593]" : "text-[#FF3B30]"}`}>
            {license?.active ? `Active · ${license.days_left} days left` : license?.status === "expired" ? "Expired" : "Inactive"}
          </div>
        </div>
      </div>

      <div>
        <div className="mb-3"><span className="font-mono-term text-xs uppercase tracking-widest text-[#888]">Choose your plan</span></div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">{sortedPlans.map((p) => <PlanCard key={p.id} plan={p} />)}</div>
      </div>

      {partnerPlans.length > 0 && (
        <div data-testid="partner-plans-section">
          <div className="mb-3 flex items-center gap-2">
            <span className="font-mono-term text-xs uppercase tracking-widest text-[#00F593]">Plan Espesyal Patnè Ou</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {partnerPlans.map((pp) => (
              <div key={pp.id} data-testid={`partner-plan-${pp.id}`} className="relative bg-[#0F0F0F] border border-[#00F593]/40 p-6 flex flex-col">
                <div className="flex items-baseline justify-between">
                  <h3 className="font-heading text-lg font-semibold">{pp.name}</h3>
                  <span className="font-mono-term text-xs text-[#888]">{pp.days}d</span>
                </div>
                <div className="mt-3 mb-4"><span className="font-heading text-3xl font-bold">${pp.price}</span></div>
                <ul className="space-y-2 flex-1 mb-5">
                  {[`${pp.days} jou aksè`, "4 Enstriman Premium", "Precision Scalping Engine", "Auto SL/TP"].map((f) => (
                    <li key={f} className="flex items-start gap-2 text-xs text-[#ccc]"><Check className="h-3.5 w-3.5 text-[#00F593] mt-0.5 shrink-0" />{f}</li>
                  ))}
                </ul>
                <Button onClick={() => payPartnerPlan(pp.id)} disabled={ppBusy === pp.id} data-testid={`buy-partner-plan-${pp.id}`}
                  className="w-full h-10 rounded-sm font-semibold bg-white text-black hover:bg-[#e5e5e5]">
                  {ppBusy === pp.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="h-4 w-4 mr-2" /> Peye ak Kat</>}
                </Button>
              </div>
            ))}
          </div>
        </div>
      )}

      {payments.length > 0 && (
        <div>
          <h2 className="font-heading text-lg font-semibold mb-3">Payment History</h2>
          <div className="border border-[#222] bg-[#0F0F0F]">
            {payments.map((p) => (
              <div key={p.id} data-testid={`payment-${p.id}`} className="flex items-center justify-between px-5 py-4 border-b border-[#222]">
                <div>
                  <div className="font-heading font-semibold text-sm">{p.plan_name} · ${p.amount}</div>
                  <div className="font-mono-term text-xs text-[#666] mt-0.5">{p.method || "BTC"} · {new Date(p.created_at).toLocaleString()}</div>
                </div>
                <StatusPill status={p.status} />
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-[#0F0F0F] border-[#222] text-white max-w-md">
          <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><Coins className="h-5 w-5 text-[#F7931A]" /> Konplete peman ou</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-4">
              <div className="border border-[#222] bg-[#0A0A0A] p-4">
                <div className="flex justify-between text-sm mb-2"><span className="text-[#888]">{selected.name}</span><span className="font-heading font-bold">${selected.price}</span></div>
                <div className="font-mono-term text-xs text-[#666]">{selected.days} days access</div>
              </div>
              <div>
                <Label className="text-xs text-[#888]">Choose currency</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {info.wallets?.map((w) => (
                    <button key={w.method} onClick={() => setMethod(w.method)} data-testid={`method-${w.method}`}
                      className={`px-3 py-1.5 font-mono-term text-xs uppercase tracking-widest border transition-colors ${method === w.method ? "bg-white text-black border-white" : "border-[#333] text-[#ccc] hover:bg-[#1A1A1A]"}`}>
                      {w.method}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <Label className="text-xs text-[#888]">Send {activeWallet?.name} ({activeWallet?.network}) to</Label>
                <div className="flex items-center gap-2 mt-1">
                  <code data-testid="crypto-address" className="flex-1 font-mono-term text-xs bg-[#0A0A0A] border border-[#222] px-3 py-2.5 truncate">{activeWallet?.address}</code>
                  <Button onClick={copyAddr} size="icon" variant="outline" className="h-10 w-10 rounded-sm border-[#333] bg-transparent shrink-0"><Copy className="h-4 w-4" /></Button>
                </div>
              </div>
              <div><Label className="text-xs text-[#888]">Transaction ID (optional)</Label><Input value={txid} onChange={(e) => setTxid(e.target.value)} data-testid="txid-input" className="mt-1 bg-[#0A0A0A] border-[#222] rounded-sm" /></div>

              <div className="flex items-start gap-2 border border-[#e0a800]/40 bg-[#e0a800]/10 px-3 py-2.5" data-testid="payment-warning">
                <AlertTriangle className="h-4 w-4 text-[#e0a800] mt-0.5 shrink-0" />
                <p className="text-xs text-[#e0c98a] leading-relaxed">
                  Voye montan egzak la nan <span className="font-semibold text-[#e0a800]">egzakteman adrès sa a</span>. Si w voye nan yon move adrès, w ap pèdi peman an. Apre peman an, voye screenshot la ban nou sou WhatsApp pou nou verifye l.
                </p>
              </div>
            </div>
          )}
          <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
            <Button onClick={payViaCard} data-testid="card-pay-button" disabled={cardBusy || !PLAN_LOOKUP[selected?.id]}
              className="w-full h-12 rounded-sm bg-white text-black hover:bg-[#e5e5e5] font-semibold">
              {cardBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><CreditCard className="h-5 w-5 mr-2" /> Peye ak Kat (Stripe) — aksè otomatik</>}
            </Button>
            <div className="flex items-center gap-3 my-1">
              <div className="h-px flex-1 bg-[#222]" /><span className="font-mono-term text-[10px] text-[#555] uppercase">oswa</span><div className="h-px flex-1 bg-[#222]" />
            </div>
            <Button onClick={payViaWhatsApp} data-testid="whatsapp-pay-button" disabled={busy}
              className="w-full h-12 rounded-sm bg-[#25D366] text-black hover:bg-[#1fb855] font-semibold">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><WhatsAppIcon className="h-5 w-5 mr-2" /> Peye ak Crypto — Voye prèv sou WhatsApp</>}
            </Button>
            <p className="text-[11px] text-[#666] text-center flex items-center justify-center gap-1"><Clock className="h-3 w-3" /> Kat = aksè otomatik. Crypto = verifikasyon jiska 24 èdtan.</p>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
